import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../../components/ui/dialog';
import { toast } from 'sonner';
import { Loader2, Plus, Trash2, Download, Search, Check, X, Eye, EyeOff, Edit2, RefreshCw, Shield, Mail, Save, Clock, Server, Database, AlertTriangle, CheckCircle, XCircle, FileText, Settings, Users, UserCheck, UserX, KeyRound, ShieldCheck } from 'lucide-react';
import { Textarea } from '../../components/ui/textarea';

import { usersAPI, adminAPI } from '../../services/api';
import { PaginationControls } from '../../components/PaginationControls';
import { useClientPagination } from '../../hooks/useClientPagination';

const ROLE_COLORS = {
  admin: 'bg-red-100 text-red-700',
  manager: 'bg-blue-100 text-blue-700',
  user: 'bg-green-100 text-green-700',
  label_operator: 'bg-purple-100 text-purple-700',
};

const STATUS_COLORS = {
  active: 'bg-green-100 text-green-700',
  pending: 'bg-amber-100 text-amber-700',
  suspended: 'bg-red-100 text-red-700',
};

export default function UsersPage() {
  const [users, setUsers] = useState([]);
  const [pendingUsers, setPendingUsers] = useState([]);
  const userPagination = useClientPagination(users);
  const [loading, setLoading] = useState(true);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectingUserId, setRejectingUserId] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [actionLoading, setActionLoading] = useState(null);
  // Admin reset-password dialog state
  const [resetPwUser, setResetPwUser] = useState(null);
  const [resetPwValue, setResetPwValue] = useState('');
  const [resetPwShow, setResetPwShow] = useState(false);
  const [resetPwSubmitting, setResetPwSubmitting] = useState(false);
  const resetPwOk = resetPwValue.length >= 8 && /\d/.test(resetPwValue);
  const openResetPasswordDialog = (user) => {
    setResetPwUser(user);
    setResetPwValue('');
    setResetPwShow(false);
  };
  const submitResetPassword = async () => {
    if (!resetPwUser || !resetPwOk) return;
    setResetPwSubmitting(true);
    try {
      const res = await usersAPI.resetPassword(resetPwUser.id, resetPwValue);
      toast.success(res.data?.message || 'Password reset successful');
      setResetPwUser(null);
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Failed to reset password';
      toast.error(typeof detail === 'string' ? detail : 'Failed to reset password');
    } finally {
      setResetPwSubmitting(false);
    }
  };

  const fetchUsers = useCallback(async () => {
    try {
      const response = await usersAPI.list();
      setUsers(response.data);
    } catch (error) {
      toast.error('Failed to fetch users');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchPendingUsers = useCallback(async () => {
    try {
      const response = await usersAPI.listPending();
      setPendingUsers(response.data);
    } catch (error) {
      console.error('Failed to fetch pending users', error);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
    fetchPendingUsers();
  }, [fetchUsers, fetchPendingUsers]);

  const handleApprove = async (userId) => {
    setActionLoading(userId);
    try {
      await usersAPI.approve(userId);
      toast.success('User approved successfully');
      fetchUsers();
      fetchPendingUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Approval failed');
    } finally {
      setActionLoading(null);
    }
  };

  const handleRejectClick = (userId) => {
    setRejectingUserId(userId);
    setRejectReason('');
    setRejectDialogOpen(true);
  };

  const handleRejectConfirm = async () => {
    if (!rejectReason.trim()) {
      toast.error('Please provide a reason for rejection');
      return;
    }
    setActionLoading(rejectingUserId);
    try {
      await usersAPI.reject(rejectingUserId, rejectReason);
      toast.success('User rejected');
      setRejectDialogOpen(false);
      fetchUsers();
      fetchPendingUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Rejection failed');
    } finally {
      setActionLoading(null);
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    try {
      await usersAPI.update(userId, { role: newRole });
      toast.success('User role updated');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Update failed');
    }
  };

  const handleStatusChange = async (userId, isActive) => {
    try {
      await usersAPI.update(userId, { is_active: isActive });
      toast.success(`User ${isActive ? 'activated' : 'deactivated'}`);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Update failed');
    }
  };

  const handleLabelAccessChange = async (userId, hasAccess) => {
    try {
      await usersAPI.update(userId, { has_label_access: hasAccess });
      toast.success(`Label Generator access ${hasAccess ? 'granted' : 'revoked'}`);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Update failed');
    }
  };

  // --- Create User Dialog ---
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState({ name: '', email: '', password: '', role: 'user', has_label_access: false });
  const [createShowPw, setCreateShowPw] = useState(false);
  const [createSubmitting, setCreateSubmitting] = useState(false);
  const createPwOk = createForm.password.length >= 8 && /\d/.test(createForm.password);
  const createValid = createForm.name.trim() && /@/.test(createForm.email) && createPwOk;
  const submitCreate = async () => {
    if (!createValid) return;
    setCreateSubmitting(true);
    try {
      const res = await usersAPI.create({
        name: createForm.name.trim(),
        email: createForm.email.trim().toLowerCase(),
        password: createForm.password,
        role: createForm.role,
        has_label_access: createForm.has_label_access,
      });
      toast.success(res.data?.message || 'User created');
      setCreateOpen(false);
      setCreateForm({ name: '', email: '', password: '', role: 'user', has_label_access: false });
      fetchUsers();
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Failed to create user';
      toast.error(typeof detail === 'string' ? detail : 'Failed to create user');
    } finally {
      setCreateSubmitting(false);
    }
  };

  // --- Delete User Dialog ---
  const [deleteUser, setDeleteUser] = useState(null);
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);
  const submitDelete = async () => {
    if (!deleteUser) return;
    setDeleteSubmitting(true);
    try {
      await usersAPI.delete(deleteUser.id);
      toast.success(`Deleted ${deleteUser.email}`);
      setDeleteUser(null);
      fetchUsers();
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Failed to delete user';
      toast.error(typeof detail === 'string' ? detail : 'Failed to delete user');
    } finally {
      setDeleteSubmitting(false);
    }
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <h1 className="text-3xl font-heading font-bold uppercase tracking-tight">
            User Management
          </h1>
          <p className="text-muted-foreground">Manage user accounts and permissions</p>
        </div>
        <Button onClick={() => setCreateOpen(true)} data-testid="add-user-btn">
          <Plus className="h-4 w-4 mr-2" />
          Add User
        </Button>
      </div>

      {/* Pending Users Section */}
      {pendingUsers.length > 0 && (
        <Card className="card-dashboard border-amber-500/50 bg-amber-500/5">
          <CardHeader>
            <div className="flex items-center gap-2">
              <Clock className="h-5 w-5 text-amber-500" />
              <CardTitle className="text-lg font-heading uppercase text-amber-600">
                Pending Approvals ({pendingUsers.length})
              </CardTitle>
            </div>
            <CardDescription>These users are waiting for your approval to access the system</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="table-wrapper">
              <Table>
                <TableHeader>
                  <TableRow className="table-header">
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Requested Role</TableHead>
                    <TableHead>Registered</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {pendingUsers.map((user) => (
                    <TableRow key={user.id} className="table-row bg-amber-500/5">
                      <TableCell className="font-medium">{user.name}</TableCell>
                      <TableCell>{user.email}</TableCell>
                      <TableCell>
                        <Badge className={ROLE_COLORS[user.role]}>
                          {user.role}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {new Date(user.created_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Button
                            size="sm"
                            className="bg-emerald-600 hover:bg-emerald-700"
                            onClick={() => handleApprove(user.id)}
                            disabled={actionLoading === user.id}
                            data-testid={`approve-user-${user.id}`}
                          >
                            {actionLoading === user.id ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <>
                                <UserCheck className="h-4 w-4 mr-1" />
                                Approve
                              </>
                            )}
                          </Button>
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => handleRejectClick(user.id)}
                            disabled={actionLoading === user.id}
                            data-testid={`reject-user-${user.id}`}
                          >
                            <UserX className="h-4 w-4 mr-1" />
                            Reject
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* All Users Section */}
      <Card className="card-dashboard">
        <CardHeader>
          <div className="flex items-center gap-2">
            <Users className="h-5 w-5 text-primary" />
            <CardTitle className="text-lg font-heading uppercase">All Users</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
            </div>
          ) : (
            <div className="table-wrapper">
              <Table>
                <TableHeader>
                  <TableRow className="table-header">
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Last Login</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {userPagination.pageRows.map((user) => (
                    <TableRow key={user.id} className="table-row">
                      <TableCell className="font-medium">{user.name}</TableCell>
                      <TableCell>{user.email}</TableCell>
                      <TableCell>
                        <Badge className={ROLE_COLORS[user.role]}>
                          {user.role}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge className={STATUS_COLORS[user.status] || (user.is_active ? 'badge-success' : 'badge-error')}>
                          {user.status || (user.is_active ? 'Active' : 'Inactive')}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {user.last_login 
                          ? new Date(user.last_login).toLocaleString() 
                          : 'Never'}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Select
                            value={user.role}
                            onValueChange={(value) => handleRoleChange(user.id, value)}
                          >
                            <SelectTrigger className="w-[140px]" data-testid={`role-select-${user.id}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="user">User</SelectItem>
                              <SelectItem value="manager">Manager</SelectItem>
                              <SelectItem value="admin">Admin</SelectItem>
                              <SelectItem value="label_operator">Label Operator</SelectItem>
                            </SelectContent>
                          </Select>
                          {/* Label Access checkbox - for non-admin, non-label_operator roles */}
                          {user.role !== 'admin' && user.role !== 'label_operator' && (
                            <label className="flex items-center gap-1 text-xs cursor-pointer" title="Grant Label Generator access">
                              <input
                                type="checkbox"
                                checked={user.has_label_access || false}
                                onChange={(e) => handleLabelAccessChange(user.id, e.target.checked)}
                                className="rounded border-gray-300"
                              />
                              <span className="text-muted-foreground">Labels</span>
                            </label>
                          )}
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleStatusChange(user.id, !user.is_active)}
                          >
                            {user.is_active ? 'Deactivate' : 'Activate'}
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openResetPasswordDialog(user)}
                            data-testid={`reset-password-btn-${user.id}`}
                            title="Reset this user's password"
                          >
                            <KeyRound className="h-4 w-4 mr-1" />
                            Reset PW
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                            onClick={() => setDeleteUser(user)}
                            data-testid={`delete-user-btn-${user.id}`}
                            title="Delete this user"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
          <PaginationControls
            page={userPagination.page}
            totalPages={userPagination.totalPages}
            total={userPagination.total}
            limit={userPagination.limit}
            onLimitChange={userPagination.setLimit}
            onPageChange={userPagination.setPage}
          />
        </CardContent>
      </Card>

      {/* Reject Dialog */}
      <Dialog open={rejectDialogOpen} onOpenChange={setRejectDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject User Registration</DialogTitle>
            <DialogDescription>
              Please provide a reason for rejecting this user's registration request.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Rejection Reason *</Label>
              <Textarea
                placeholder="Enter reason for rejection..."
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleRejectConfirm}
              disabled={actionLoading}
            >
              {actionLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Confirm Rejection
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Admin Force-Reset Password Dialog */}
      <Dialog open={!!resetPwUser} onOpenChange={(open) => { if (!open) setResetPwUser(null); }}>
        <DialogContent className="max-w-md" data-testid="reset-password-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <KeyRound className="h-5 w-5 text-indigo-600" />
              Reset Password
            </DialogTitle>
            <DialogDescription>
              Set a new password for <span className="font-semibold">{resetPwUser?.email}</span>.
              The user will be forced to change it again on their next sign-in.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-1">
              <Label>New password</Label>
              <div className="relative">
                <Input
                  type={resetPwShow ? 'text' : 'password'}
                  value={resetPwValue}
                  onChange={(e) => setResetPwValue(e.target.value)}
                  autoComplete="new-password"
                  data-testid="reset-password-input"
                />
                <button
                  type="button"
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  onClick={() => setResetPwShow((v) => !v)}
                  aria-label="toggle password visibility"
                  tabIndex={-1}
                >
                  {resetPwShow ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              <p className={`text-xs flex items-center gap-1 ${resetPwValue && !resetPwOk ? 'text-rose-600' : 'text-slate-500'}`}>
                <ShieldCheck className="h-3 w-3" />
                At least 8 characters, with at least one number.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResetPwUser(null)} disabled={resetPwSubmitting}>
              Cancel
            </Button>
            <Button
              onClick={submitResetPassword}
              disabled={!resetPwOk || resetPwSubmitting}
              data-testid="reset-password-submit"
            >
              {resetPwSubmitting ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <KeyRound className="h-4 w-4 mr-1" />}
              Reset Password
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create User Dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-md" data-testid="create-user-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="h-5 w-5 text-indigo-600" />
              Add New User
            </DialogTitle>
            <DialogDescription>
              Admin-created users are auto-approved active. The user can sign in immediately with the password you set here.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-1">
              <Label>Name *</Label>
              <Input
                value={createForm.name}
                onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
                data-testid="create-user-name"
                placeholder="e.g. John Doe"
              />
            </div>
            <div className="space-y-1">
              <Label>Email *</Label>
              <Input
                type="email"
                value={createForm.email}
                onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
                data-testid="create-user-email"
                placeholder="user@example.com"
              />
            </div>
            <div className="space-y-1">
              <Label>Password *</Label>
              <div className="relative">
                <Input
                  type={createShowPw ? 'text' : 'password'}
                  value={createForm.password}
                  onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })}
                  autoComplete="new-password"
                  data-testid="create-user-password"
                />
                <button
                  type="button"
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  onClick={() => setCreateShowPw((v) => !v)}
                  aria-label="toggle password visibility"
                  tabIndex={-1}
                >
                  {createShowPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              <p className={`text-xs flex items-center gap-1 ${createForm.password && !createPwOk ? 'text-rose-600' : 'text-slate-500'}`}>
                <ShieldCheck className="h-3 w-3" />
                At least 8 characters, with at least one number.
              </p>
            </div>
            <div className="space-y-1">
              <Label>Role</Label>
              <Select value={createForm.role} onValueChange={(v) => setCreateForm({ ...createForm, role: v })}>
                <SelectTrigger data-testid="create-user-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="user">User</SelectItem>
                  <SelectItem value="manager">Manager</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="label_operator">Label Operator</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)} disabled={createSubmitting}>
              Cancel
            </Button>
            <Button onClick={submitCreate} disabled={!createValid || createSubmitting} data-testid="create-user-submit">
              {createSubmitting ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Plus className="h-4 w-4 mr-1" />}
              Create User
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete User Confirmation */}
      <Dialog open={!!deleteUser} onOpenChange={(open) => { if (!open) setDeleteUser(null); }}>
        <DialogContent className="max-w-md" data-testid="delete-user-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-rose-700">
              <Trash2 className="h-5 w-5" />
              Delete User
            </DialogTitle>
            <DialogDescription>
              Permanently delete <span className="font-semibold">{deleteUser?.email}</span>?
              This cannot be undone. The user will lose access immediately and audit history will note the deletion.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteUser(null)} disabled={deleteSubmitting}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={submitDelete}
              disabled={deleteSubmitting}
              data-testid="delete-user-confirm"
            >
              {deleteSubmitting ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Trash2 className="h-4 w-4 mr-1" />}
              Delete User
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

